import React, { useEffect, useMemo, useState } from "react";
import "./Clothes.css";
import { useNavigate } from "react-router-dom";

import saree from "../assets/clothes/saree.png";
import bharathanatyamDanceSaree from "../assets/clothes/bharathanatyam_dance_saree.png";
import kurtha from "../assets/clothes/kurtha.png";
import vetti from "../assets/clothes/vetti.png";
import panjavi from "../assets/clothes/panjavi.png";
import kandayanOutfitMen from "../assets/clothes/kandayan_outfit_men.png";
import lungi from "../assets/clothes/lungi.png";
import osariya from "../assets/clothes/osariya.png";

import cardBack from "../assets/clothes/faced_down_card.png";
import cardFront from "../assets/clothes/faced_up_card.png";
import star from "../assets/clothes/star.png";

//master list of all clothing items with their dispaly title and image
const CLOTHES = [
  { key: "saree",                  title: "Saree",                img: saree },
  { key: "bharathanatyam_dance_saree", title: "Dance Saree",      img: bharathanatyamDanceSaree },
  { key: "kurtha",                 title: "Kurtha",               img: kurtha },
  { key: "vetti",                  title: "Vetti",                img: vetti },
  { key: "panjavi",                title: "Panjavi",              img: panjavi },
  { key: "kandayan_outfit_men",    title: "Kandyan Outfit (Men)", img: kandayanOutfitMen },
  { key: "lungi",                  title: "Lungi",                img: lungi },
  { key: "osariya",                title: "Osariya",              img: osariya },
];

// extra info shown in the modal afte the player matches a pair
const INFO = {
  saree: {
    what: "A long piece of fabric wrapped and pleated into a saree.",
    who: "Worn by women and girls.",
    occasions: "Weddings, festivals and special events.",
  },
  bharathanatyam_dance_saree: {
    what: "A classical costume which is made for dancing.",
    who: "Worn by dancers.",
    occasions: "Performances, cultural shows, festivals.",
  },
  kurtha: {
    what: "A long cultural top.",
    who: "Worn by boys and men.",
    occasions: "Weddings, festivals and celebrations.",
  },
  vetti: {
    what: "A traditional cloth wrapped around the waist.",
    who: "Worn by men.",
    occasions: "Weddings, fesitivals and special events.",
  },
  panjavi: {
    what: "A traditional outfit paired with a beautiful shawl.",
    who: "Worn by women and girls.",
    occasions: "Everyday wear",
  },
  kandayan_outfit_men: {
    what: "A traditional outfit worn by men.",
    who: "Men, usually worn by grooms.",
    occasions: "Weddings, fesitivals and special events.",
  },
  lungi: {
    what: "A blue cloth wrapped around the waist.",
    who: "Worn by men at home.",
    occasions: "Casual, daily home wear.",
  },
  osariya: {
    what: "A traditional saree with a frilled waist and puffed sleves",
    who: "Worn by women and girls",
    occasions: "Weddings, festivals, and special events",
  },
};

//difficulty level names kept as constants to avoid raw string comparisns
const DIFFICULTY = {
  NONE:   "none",
  EASY:   "easy",
  MEDIUM: "medium",
  HARD:   "hard",
};

// how many pairs are on the board for each difficulty
const PAIR_COUNTS = {
  easy:   4,
  medium: 5,
  hard:   6,
};

//fisher-yates shuffle, returns a new array and leaves the original untouched
function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// picks the right number of clothes for the difficulty and creates two cards per item
function buildDeck(diff) {
  const count = PAIR_COUNTS[diff] ?? CLOTHES.length;
  const selected = shuffle(CLOTHES).slice(0, count);

  // flatMap creates the pair, each card gets a uniqe uid so react can tell them apart
  const pairs = selected.flatMap((item) => [
    { uid: `${item.key}-a-${Math.random()}`, key: item.key, title: item.title, img: item.img, matched: false },
    { uid: `${item.key}-b-${Math.random()}`, key: item.key, title: item.title, img: item.img, matched: false },
  ]);

  return shuffle(pairs);
}

export default function Clothes() {
  const navigate = useNavigate();

  // which difficulty is active, starts as NONE so the picker screen shows first
  const [difficulty, setDifficulty] = useState(DIFFICULTY.NONE);

  //the full array of card objects for the current round
  const [deck, setDeck] = useState([]);

  // the first card the player flipped this turn
  const [first, setFirst] = useState(null);

  // the second card the player flipped this turn
  const [second, setSecond] = useState(null);

  // lock stops the player clicking more cards while a match check is in progress
  const [lock, setLock] = useState(false);

  // key of the clothing item whose info modal is currently open, null means closed
  const [modalKey, setModalKey] = useState(null);

  // flips to true when all pairs are matched so the win modal shows
  const [showWinModal, setShowWinModal] = useState(false);

  // count of matched cards, divide by 2 to get matched pairs
  const matchedCount = useMemo(() => deck.filter((card) => card.matched).length, [deck]);
  const totalPairs = deck.length / 2;

  // sets up a fresh round for the chosen difficulty
  function startGame(diff) {
    setDifficulty(diff);
    setDeck(buildDeck(diff));
    setFirst(null);
    setSecond(null);
    setLock(false);
    setModalKey(null);
    setShowWinModal(false);
  }

  // goes back to the difficulty picker screen
  function startNewGame() {
    setDifficulty(DIFFICULTY.NONE);
    setDeck([]);
    setFirst(null);
    setSecond(null);
    setLock(false);
    setModalKey(null);
    setShowWinModal(false);
  }

  //card should show its face if it is matched or currently selected
  function isFlipped(card) {
    return card.matched || card.uid === first?.uid || card.uid === second?.uid;
  }

  //handles a player tapping a card
  function handleCardClick(card) {
    if (lock) return;
    if (card.matched) return;

    // ignore if the player clicks the same card twice
    if (card.uid === first?.uid) return;

    // first click of the  turn
    if (!first) return setFirst(card);

    // second click of the turn, lock the board while the result is processed
    if (!second) {
      setSecond(card);
      setLock(true);
    }
  }

  // runs whenever both first and second are set, checks for a match
  useEffect(() => {
    if (!first || !second) return;

    const isMatch = first.key === second.key;

    if (isMatch) {
      // mark both cards as macther in the deck
      setDeck((prev) =>
        prev.map((card) => (card.key === first.key ? { ...card, matched: true } : card))
      );

      //short delay before openign the info modal so the flip animation finishes
      const timer = setTimeout(() => {
        setModalKey(first.key);
        setFirst(null);
        setSecond(null);
        setLock(false);
      }, 350);

      return () => clearTimeout(timer);
    } else {
      //if not a match, flip both cards back after a moment
      const timer = setTimeout(() => {
        setFirst(null);
        setSecond(null);
        setLock(false);
      }, 850);

      return () => clearTimeout(timer);
    }
  }, [first, second]);

  return (
    <div className="clothesPage">
      <header className="clothesHeader">
        <div className="puzzleHomeCorner">
          <button
            className="homeBtn"
            onClick={() => navigate("/loading", { state: { to: "/home" } })}
            aria-label="Go Home"
            title="Go Home"
            type="button"
          >

            <svg
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="homeBtn__icon"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
                fill="#ffffff"
              />
            </svg>
          </button>
        </div>

        <h1 className="clothesTitle">Clothes Match!</h1>
        <p className="clothesSubtitle">Flip two cards. Match to learn about Sri Lankan clothing</p>
      </header>

      {/* difficulty picker shown before the game starts */}
      {difficulty === DIFFICULTY.NONE ? (
        <div className="difficultyScreen">
          <p className="difficultyPrompt">Choose your difficulty</p>
          <div className="difficultyRow">
            <button className="diffBtn diffBtn--easy" onClick={() => startGame("easy")} type="button">
              <span className="diffLabel">Easy</span>
              <span className="diffDesc">4 pairs</span>
              <div className="difficultyStars">
                <img src={star} alt="1 star difficulty" className="difficultyStar" />
              </div>
            </button>

            <button className="diffBtn diffBtn--medium" onClick={() => startGame("medium")} type="button">
              <span className="diffLabel">Medium</span>
              <span className="diffDesc">5 pairs</span>
              <div className="difficultyStars">
                <img src={star} alt="star" className="difficultyStar" />
                <img src={star} alt="star" className="difficultyStar" />
              </div>
            </button>

            <button className="diffBtn diffBtn--hard" onClick={() => startGame("hard")} type="button">
              <span className="diffLabel">Hard</span>
              <span className="diffDesc">6 pairs</span>
              <div className="difficultyStars">
                <img src={star} alt="star" className="difficultyStar" />
                <img src={star} alt="star" className="difficultyStar" />
                <img src={star} alt="star" className="difficultyStar" />
              </div>
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* top  bar shows match progress and the cahnge difficulty button */}
          <div className="clothesTopBar">
            <span className="scorePill">
              Matches: <b>{Math.floor(matchedCount / 2)}</b> / {totalPairs}
            </span>
            <button className="clothesBtn" onClick={startNewGame} type="button">
              Change Difficulty
            </button>
          </div>

          <main className="clothesBoardWrap">
            <div className="clothesBoard">
              {deck.map((card) => {
                const flipped = isFlipped(card);
                return (
                  <button
                    key={card.uid}
                    className={`memoryCard ${flipped ? "isFlipped" : ""} ${card.matched ? "isMatched" : ""}`}
                    onClick={() => handleCardClick(card)}
                    type="button"
                  >
                    <div className="memoryCardInner">
                      {/* back layer fades out when the card flips */}
                      <img
                        className={`cardLayer cardBackLayer ${flipped ? "hideLayer" : "showLayer"}`}
                        src={cardBack}
                        alt="Card back"
                        draggable="false"
                      />
                      {/* front layyer shows the clothing image on top of the card art */}
                      <div className={`cardLayer cardFrontLayer ${flipped ? "showLayer" : "hideLayer"}`}>
                        <img className="cardFrontBg" src={cardFront} alt="Card front" draggable="false" />
                        <img className="cardClothing" src={card.img} alt={card.title} draggable="false" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </main>
        </>
      )}

      {/* info modal shown after each successful match */}
      {modalKey && (
        <div className="modalOverlay" role="dialog" aria-modal="true">
          <div className="modalCard">
            <div className="modalHeader">
              <h2 className="modalTitle">{CLOTHES.find((item) => item.key === modalKey)?.title}</h2>
              <button
                className="modalClose"
                onClick={() => {
                  setModalKey(null);
                  // open the win modal if all pairs are now matched
                  if (deck.length > 0 && matchedCount === deck.length) {
                    setShowWinModal(true);
                  }
                }}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="modalBody">
              <div className="modalImageWrap">
                <img src={CLOTHES.find((item) => item.key === modalKey)?.img} alt="Matched clothing" />
              </div>

              <div className="modalText">
                <div className="modalLine">
                  <span className="modalLabel">What is it?</span>
                  <p>{INFO[modalKey]?.what}</p>
                </div>
                <div className="modalLine">
                  <span className="modalLabel">Who wears it?</span>
                  <p>{INFO[modalKey]?.who}</p>
                </div>
                <div className="modalLine">
                  <span className="modalLabel">When is it worn?</span>
                  <p>{INFO[modalKey]?.occasions}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* win modal shown once every pair on the board is matched */}
      {showWinModal && (
        <div className="modalOverlay" role="dialog" aria-modal="true">
          <div className="winModal">
            <h2 className="winModalTitle">Well Done!</h2>
            <p className="winModalText">
              You matched all {totalPairs} pairs. Amazing work, explorer!
            </p>

            <div className="winModalActions">
              <button className="clothesBtn" onClick={startNewGame} type="button">
                Change Difficulty
              </button>

              <button
                className="clothesBtn winHomeBtn"
                onClick={() => navigate("/loading", { state: { to: "/home" } })}
                type="button"
              >
                Home
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}